import { useEffect, useRef } from "react";
import { useForm, FormProvider, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LGoButton, LGoSheet } from "@/ui/branded";
import { refusedFieldMessage } from "@/lib/form-refusal";
import { useActivityImageUpload } from "@/features/activities/hooks/useActivityImageUpload";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import {
  activityFormSchema,
  type ActivityFormData,
} from "@/features/activities/form-schema";
import { definitionsToRows } from "@/features/custom-fields/form-schema";
import type { Activity, ActivitySubcategory } from "@/domain/activity/types";
import { clampedToSlotEnd, saleCloseOf, toAuthorableBookingMode } from "@/domain/activity/rules";
import { ActivityFormFields } from "./ActivityFormFields";

interface ActivityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity?: Activity | null;
  subcategories: ActivitySubcategory[];
  onSubmit: (data: ActivityFormData) => Promise<void>;
  isSubmitting: boolean;
}

const emptyValues: ActivityFormData = {
  activity_name: "",
  // RHF needs concrete defaults — we use undefined for "unset" numerics so the
  // Zod required_error fires on submit rather than letting NaN through.
  activity_subcategory_id: undefined as unknown as number,
  booking_mode: "session",
  price_original: undefined as unknown as number,
  image_url: "",
  description: "",
  custom_fields: [],
  default_price_discounted: undefined,
  default_capacity: undefined,
  sale_lead_time_hours: undefined,
  sale_end_offset_minutes: undefined,
  sale_end_anchor: undefined,
  default_duration_minutes: undefined,
  requires_confirmation: false,
};

function activityToFormValues(activity: Activity): ActivityFormData {
  const storedSaleClose = saleCloseOf(activity);
  const saleClose = storedSaleClose && clampedToSlotEnd(storedSaleClose);
  return {
    activity_name: activity.name,
    activity_subcategory_id: activity.subcategoryId ?? (undefined as unknown as number),
    booking_mode: toAuthorableBookingMode(activity.bookingMode),
    price_original: activity.priceOriginal ?? (undefined as unknown as number),
    image_url: activity.imageUrl ?? "",
    description: activity.description ?? "",
    custom_fields: definitionsToRows(activity.customFields),
    default_price_discounted: activity.defaultPriceDiscounted ?? undefined,
    default_capacity: activity.defaultCapacity ?? undefined,
    sale_lead_time_hours: activity.saleLeadTimeHours ?? undefined,
    sale_end_offset_minutes: saleClose?.offsetMinutes,
    sale_end_anchor: saleClose?.anchor,
    default_duration_minutes: activity.defaultDurationMinutes ?? undefined,
    requires_confirmation: activity.requiresConfirmation,
  };
}

export type { ActivityFormData };

export default function ActivityFormDialog({
  open,
  onOpenChange,
  activity,
  subcategories,
  onSubmit,
  isSubmitting,
}: ActivityFormDialogProps) {
  const isEditing = !!activity;
  const { businessId } = useUserBusiness();
  const { deleteImage } = useActivityImageUpload();

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activityFormSchema),
    mode: "onBlur",
    defaultValues: emptyValues,
  });

  // Tracks the DB-persisted image URL when the dialog opens (what the activity
  // had); used to clean up the prior upload after a successful replace.
  const persistedUrlRef = useRef<string | null>(null);
  const sessionUploadsRef = useRef<string[]>([]);
  const fieldsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    if (activity) {
      form.reset(activityToFormValues(activity));
      persistedUrlRef.current = activity.imageUrl ?? null;
    } else {
      form.reset(emptyValues);
      persistedUrlRef.current = null;
    }
    sessionUploadsRef.current = [];
  }, [activity, open, form]);

  // The order the partner reads the form in, which is the DOM's and not the
  // schema's. Both the scroll and the sentence beside the button take their
  // "first refused field" from here, so they can never name different ones.
  const fieldNamesInReadingOrder = () =>
    Array.from(fieldsRef.current?.querySelectorAll<HTMLElement>("[data-field]") ?? [])
      .map((node) => node.dataset.field ?? "")
      .filter(Boolean);

  // RHF focuses the first refused input, but the image field has none to focus,
  // and a refusal the partner has scrolled past reads as "nothing saves".
  const revealFirstRefusedField = (errors: FieldErrors<ActivityFormData>) => {
    const refusedNames = new Set(Object.keys(errors));
    const fieldNodes = fieldsRef.current?.querySelectorAll<HTMLElement>("[data-field]") ?? [];
    const topmostRefused = Array.from(fieldNodes).find((node) =>
      refusedNames.has(node.dataset.field ?? ""),
    );
    topmostRefused?.scrollIntoView({ block: "center" });
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await onSubmit(data);
      // After a successful submit, clean up the previously persisted image
      // if it was replaced.
      const persisted = persistedUrlRef.current;
      const current = data.image_url || null;
      if (persisted && persisted !== current) {
        deleteImage(persisted).catch((err) => console.error("cleanup failed", err));
      }
      // Defensive: any earlier session uploads other than the final committed one.
      const finalUploaded = sessionUploadsRef.current;
      for (let i = 0; i < finalUploaded.length - 1; i++) {
        deleteImage(finalUploaded[i]).catch(() => {});
      }
      sessionUploadsRef.current = [];
      persistedUrlRef.current = current;
    } catch (err) {
      console.error("activity submit failed", err);
    }
  }, revealFirstRefusedField);

  const refusal = form.formState.isSubmitted
    ? refusedFieldMessage(form.formState.errors, fieldNamesInReadingOrder())
    : null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Dialog closing without a successful submit — clean up orphan uploads.
      const persisted = persistedUrlRef.current;
      for (const url of sessionUploadsRef.current) {
        if (url !== persisted) {
          deleteImage(url).catch(() => {});
        }
      }
      sessionUploadsRef.current = [];
    }
    onOpenChange(nextOpen);
  };

  return (
    <LGoSheet
      open={open}
      onOpenChange={handleOpenChange}
      title={isEditing ? "Upravit šablonu slev" : "Nová šablona slev"}
      description={
        isEditing
          ? "Upravte údaje šablony slev a uložte změny."
          : "Vytvořte novou šablonu slev pro váš podnik."
      }
      width="md"
      onSubmit={handleSubmit}
      footer={
        <>
          {refusal && (
            <p role="alert" className="mr-auto text-sm text-destructive">
              {refusal}
            </p>
          )}
          <LGoButton
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Zrušit
          </LGoButton>
          <LGoButton type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? "Uložit změny" : "Vytvořit šablonu slev"}
          </LGoButton>
        </>
      }
    >
      <FormProvider {...form}>
        <div ref={fieldsRef}>
          <ActivityFormFields
            subcategories={subcategories}
            businessId={businessId}
            isSubmitting={isSubmitting}
            onPendingCleanup={(urls) => {
              sessionUploadsRef.current = urls;
            }}
          />
        </div>
      </FormProvider>
    </LGoSheet>
  );
}
